<template>
  <div class="admin-dashboard">
    <!-- Top Bar -->
    <header class="admin-header">
      <div class="header-left">
        <span class="header-logo">🎭</span>
        <h1 class="header-title">Babilonia Admin</h1>
      </div>
      <div class="header-right">
        <template v-if="activeTab === 'analytics'">
          <select v-model="selectedDays" @change="fetchData" class="period-select">
            <option :value="7">Últimos 7 días</option>
            <option :value="30">Últimos 30 días</option>
            <option :value="90">Últimos 90 días</option>
          </select>
          <button @click="fetchData" class="refresh-btn" :class="{ spinning: loading }">🔄</button>
        </template>
        <router-link to="/boleteria" class="logout-btn" style="text-decoration:none">🎫 Boletería</router-link>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <!-- Tab Nav -->
    <nav class="tab-nav">
      <button v-for="tab in visibleTabs" :key="tab.id" class="tab-btn"
        :class="{ active: activeTab === tab.id }" @click="switchTab(tab.id)">
        {{ tab.label }}
      </button>
    </nav>

    <!-- ── ANALYTICS TAB ── -->
    <div v-if="activeTab === 'analytics'">
      <div v-if="loading && !data" class="loading-screen">
        <div class="loader"></div>
        <p>Cargando datos...</p>
      </div>
      <main v-else-if="data" class="dashboard-content">
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-icon">👁️</div>
            <div class="kpi-info">
              <span class="kpi-value">{{ data.total_views }}</span>
              <span class="kpi-label">Visitas Totales</span>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon">👥</div>
            <div class="kpi-info">
              <span class="kpi-value">{{ data.unique_visitors }}</span>
              <span class="kpi-label">Visitantes Únicos</span>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon">📄</div>
            <div class="kpi-info">
              <span class="kpi-value">{{ data.pages.length }}</span>
              <span class="kpi-label">Páginas Visitadas</span>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-icon">🖱️</div>
            <div class="kpi-info">
              <span class="kpi-value">{{ totalSponsorClicks }}</span>
              <span class="kpi-label">Clicks en Sponsors</span>
            </div>
          </div>
        </div>

        <div class="charts-row">
          <div class="chart-card chart-wide">
            <h3 class="chart-title">📈 Visitas por Día</h3>
            <div class="chart-container"><canvas ref="dailyChart"></canvas></div>
          </div>
        </div>

        <div class="charts-row charts-two-col">
          <div class="chart-card">
            <h3 class="chart-title">📊 Páginas más Visitadas</h3>
            <div class="ranking-list">
              <div v-for="(page, i) in data.pages.slice(0, 10)" :key="i" class="ranking-item">
                <span class="ranking-pos">{{ i + 1 }}</span>
                <span class="ranking-name">{{ formatPath(page.path) }}</span>
                <div class="ranking-bar-wrap">
                  <div class="ranking-bar" :style="{ width: barWidth(page.views, maxPageViews) + '%' }"></div>
                </div>
                <span class="ranking-value">{{ page.views }}</span>
              </div>
              <p v-if="data.pages.length === 0" class="no-data">Sin datos</p>
            </div>
          </div>
          <div class="chart-card">
            <h3 class="chart-title">🌐 Fuentes de Tráfico</h3>
            <div class="chart-container"><canvas ref="sourcesChart"></canvas></div>
            <div class="source-legend">
              <div v-for="(src, i) in data.traffic_sources" :key="i" class="legend-item">
                <span class="legend-dot" :style="{ background: sourceColors[i % sourceColors.length] }"></span>
                <span class="legend-label">{{ src.source }}</span>
                <span class="legend-value">{{ src.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="charts-row charts-two-col">
          <div class="chart-card">
            <h3 class="chart-title">🏆 Clicks por Sponsor</h3>
            <div class="ranking-list">
              <div v-for="(sponsor, i) in data.sponsor_clicks" :key="i" class="ranking-item">
                <span class="ranking-pos">{{ i + 1 }}</span>
                <span class="ranking-name">{{ sponsor.name }}</span>
                <div class="ranking-bar-wrap">
                  <div class="ranking-bar ranking-bar-gold" :style="{ width: barWidth(sponsor.clicks, maxSponsorClicks) + '%' }"></div>
                </div>
                <span class="ranking-value">{{ sponsor.clicks }}</span>
              </div>
              <p v-if="data.sponsor_clicks.length === 0" class="no-data">Sin clicks aún</p>
            </div>
          </div>
          <div class="chart-card">
            <h3 class="chart-title">📱 Dispositivos</h3>
            <div class="chart-container"><canvas ref="deviceChart"></canvas></div>
            <div class="source-legend">
              <div v-for="(dev, i) in (data.devices || [])" :key="i" class="legend-item">
                <span class="legend-dot" :style="{ background: deviceColors[i % deviceColors.length] }"></span>
                <span class="legend-label">{{ dev.device }}</span>
                <span class="legend-value">{{ dev.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="charts-row charts-two-col">
          <div class="chart-card">
            <h3 class="chart-title">💻 Sistema Operativo</h3>
            <div class="ranking-list">
              <div v-for="(os, i) in (data.operating_systems || [])" :key="i" class="ranking-item">
                <span class="ranking-pos">{{ i + 1 }}</span>
                <span class="ranking-name">{{ os.os }}</span>
                <div class="ranking-bar-wrap">
                  <div class="ranking-bar ranking-bar-blue" :style="{ width: barWidth(os.count, maxOsCount) + '%' }"></div>
                </div>
                <span class="ranking-value">{{ os.count }}</span>
              </div>
            </div>
          </div>
          <div class="chart-card">
            <h3 class="chart-title">🌐 Navegador</h3>
            <div class="ranking-list">
              <div v-for="(b, i) in (data.browsers || [])" :key="i" class="ranking-item">
                <span class="ranking-pos">{{ i + 1 }}</span>
                <span class="ranking-name">{{ b.browser }}</span>
                <div class="ranking-bar-wrap">
                  <div class="ranking-bar ranking-bar-green" :style="{ width: barWidth(b.count, maxBrowserCount) + '%' }"></div>
                </div>
                <span class="ranking-value">{{ b.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="charts-row charts-two-col">
          <div class="chart-card">
            <h3 class="chart-title">🕐 Distribución por Hora</h3>
            <div class="chart-container"><canvas ref="hourlyChart"></canvas></div>
          </div>
          <div class="chart-card empty-card"></div>
        </div>

        <p class="last-updated">Última actualización: {{ lastUpdated }}</p>
      </main>
      <div v-else class="error-screen">
        <p>Error al cargar datos. <button @click="fetchData" class="retry-btn">Reintentar</button></p>
      </div>
    </div>

    <!-- ── SUSCRIPTORES TAB ── -->
    <div v-if="activeTab === 'suscriptores'" class="dashboard-content">
      <div class="tab-header">
        <h2 class="tab-title">📧 Suscriptores</h2>
        <button class="btn-export" @click="exportSuscriptores">⬇ Exportar CSV</button>
      </div>
      <div v-if="suscriptoresLoading" class="tab-loading">Cargando...</div>
      <div v-else>
        <div class="filters-bar">
          <div class="filter-group">
            <label class="filter-label">Fuente</label>
            <select v-model="filterSource" class="filter-select">
              <option value="">Todas</option>
              <option v-for="src in availableSources" :key="src" :value="src">{{ src }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Estado</label>
            <select v-model="filterActive" class="filter-select">
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Buscar</label>
            <input v-model="filterSearch" class="filter-input" placeholder="Email o nombre..." />
          </div>
          <button v-if="filterSource || filterActive || filterSearch" class="btn-clear-filters" @click="clearFilters">✕ Limpiar</button>
        </div>
        <p class="tab-count">
          Mostrando <strong>{{ filteredSuscriptores.length }}</strong> de <strong>{{ suscriptores.length }}</strong> contactos
          (activos: <strong>{{ filteredSuscriptores.filter(s => s.wants_news).length }}</strong>)
        </p>
        <table class="data-table">
          <thead>
            <tr>
              <th>Email</th><th>Nombre</th><th>Teléfono</th><th>Fuente</th><th>Activo</th><th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in filteredSuscriptores" :key="s.id">
              <td>{{ s.email }}</td>
              <td>{{ s.first_name || '' }} {{ s.last_name || '' }}</td>
              <td>{{ s.phone || '—' }}</td>
              <td><span class="source-badge">{{ s.source }}</span></td>
              <td><span :class="s.wants_news ? 'status-active' : 'status-inactive'">{{ s.wants_news ? 'Sí' : 'No' }}</span></td>
              <td>{{ formatDate(s.created_at) }}</td>
            </tr>
            <tr v-if="filteredSuscriptores.length === 0">
              <td colspan="6" style="text-align:center;color:#555;padding:20px;">Sin resultados</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── USUARIOS TAB ── -->
    <div v-if="activeTab === 'usuarios'" class="dashboard-content">
      <div class="tab-header">
        <h2 class="tab-title">👥 Usuarios</h2>
        <button class="btn-invite" @click="showInviteModal = true">+ Invitar usuario</button>
      </div>
      <div v-if="usuariosLoading" class="tab-loading">Cargando...</div>
      <div v-else>
        <table class="data-table">
          <thead>
            <tr>
              <th>Email</th><th>Nombre</th><th>Rol</th><th>Permisos</th><th>Estado</th><th>Último acceso</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in usuarios" :key="u.id">
              <td>{{ u.email }}</td>
              <td>{{ u.first_name || '' }} {{ u.last_name || '' }}</td>
              <td><span class="role-badge" :class="u.role">{{ u.role }}</span></td>
              <td>
                <span v-for="p in u.permissions" :key="p" class="perm-tag">{{ p }}</span>
                <span v-if="!u.permissions || u.permissions.length === 0" class="no-perms">—</span>
              </td>
              <td><span :class="u.is_active ? 'status-active' : 'status-pending'">{{ u.is_active ? 'Activo' : 'Pendiente' }}</span></td>
              <td>{{ u.last_login_at ? formatDate(u.last_login_at) : '—' }}</td>
              <td>
                <button class="btn-edit-user" @click="openEditUser(u)" title="Editar">✏️</button>
                <button v-if="u.email !== currentUserEmail" class="btn-delete-user" @click="deleteUser(u)" title="Eliminar">✕</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── FUNCIONES TAB ── -->
    <div v-if="activeTab === 'funciones'" class="dashboard-content">
      <div class="tab-header">
        <h2 class="tab-title">🎭 Funciones</h2>
        <button class="btn-primary" @click="openCreateModal">+ Nueva Función</button>
      </div>
      <div v-if="funcionesLoading" class="loading-screen">
        <div class="loader"></div>
        <p>Cargando funciones...</p>
      </div>
      <div v-else class="funciones-table-wrap">
        <table class="funciones-table">
          <thead>
            <tr>
              <th>ID</th><th>Título</th><th>Fecha</th><th>Venue</th><th>Precio</th><th>Festival</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in funciones" :key="f.id" :class="{ 'row-past': isPast(f.date) }">
              <td class="td-id">{{ f.id }}</td>
              <td class="td-titulo">{{ f.titulo }}<span v-if="f.origen" class="td-origen">— {{ f.origen }}</span></td>
              <td class="td-fecha">{{ f.fecha_display }}</td>
              <td class="td-venue">{{ f.venue ? f.venue.name : '—' }}</td>
              <td class="td-precio">${{ f.precio }}</td>
              <td class="td-festival">
                <span :class="f.festival ? 'badge-yes' : 'badge-no'">{{ f.festival ? 'Sí' : 'No' }}</span>
              </td>
              <td class="td-actions">
                <button class="btn-edit" @click="openEditModal(f)">✏️ Editar</button>
                <button class="btn-delete" @click="confirmDelete(f)">🗑️ Eliminar</button>
              </td>
            </tr>
            <tr v-if="funciones.length === 0">
              <td colspan="7" class="no-data" style="text-align:center; padding: 30px;">Sin funciones</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── HORAS TAB ── -->
    <div v-if="activeTab === 'horas'" class="dashboard-content">
      <div class="tab-header">
        <h2 class="tab-title">⏱ Horas trabajadas</h2>
        <router-link to="/horas" class="btn-primary" style="text-decoration:none">Ir a Registro →</router-link>
      </div>

      <!-- Filter -->
      <div class="horas-filter-bar">
        <button
          v-for="f in [{ id: 'week', label: 'Esta semana' }, { id: 'month', label: 'Este mes' }, { id: 'all', label: 'Todo' }]"
          :key="f.id"
          class="horas-filter-btn"
          :class="{ active: horasFilter === f.id }"
          @click="horasFilter = f.id; fetchHoras()"
        >{{ f.label }}</button>
      </div>

      <div v-if="horasLoading" class="tab-loading">Cargando...</div>

      <div v-else-if="horasSummary.length === 0" class="no-data" style="padding: 40px 0; text-align: center;">
        Sin registros para el período seleccionado.
      </div>

      <div v-else>
        <!-- Total banner -->
        <div class="horas-total-banner">
          <span class="horas-total-label">Total del equipo</span>
          <span class="horas-total-value">
            {{ horasSummary.reduce((s, u) => s + u.total_hours, 0).toFixed(1) }}h
          </span>
        </div>

        <!-- Per-member cards -->
        <div class="horas-members-grid">
          <div v-for="member in horasSummary" :key="member.user_id" class="horas-member-card">
            <div class="horas-member-name">{{ member.user_name || 'Sin nombre' }}</div>
            <div class="horas-member-hours">{{ member.total_hours.toFixed(1) }}<span class="horas-unit">h</span></div>
            <div class="horas-member-entries">{{ member.entry_count }} entradas</div>
            <div class="horas-bar-wrap">
              <div
                class="horas-bar"
                :style="{ width: (member.total_hours / horasSummary[0].total_hours * 100).toFixed(1) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── FUNCION MODAL (create/edit) ── -->
    <div v-if="showFuncionModal" class="funcion-modal-overlay" @click.self="closeFuncionModal">
      <div class="funcion-modal">
        <div class="modal-header">
          <h3>{{ editingFuncion ? 'Editar Función' : 'Nueva Función' }}</h3>
          <button class="modal-close" @click="closeFuncionModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Título *</label>
              <input v-model="form.titulo" type="text" class="form-input" placeholder="Nombre del espectáculo">
            </div>
            <div class="form-group">
              <label>Origen</label>
              <input v-model="form.origen" type="text" class="form-input" placeholder="País / Ciudad / Compañía">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Fecha (dd/mm/aaaa) *</label>
              <input v-model="form.date_display_input" type="text" class="form-input" placeholder="20/03/2026"
                @input="autoFormatDate" @blur="parseDateInput" maxlength="10">
              <span v-if="form.date_error" class="field-error">{{ form.date_error }}</span>
            </div>
            <div class="form-group">
              <label>Fecha display *</label>
              <input v-model="form.fecha_display" type="text" class="form-input" placeholder="Ej: Viernes 20 de Marzo - 21 hs">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Ubicación</label>
              <select v-model="form.venue_name" @change="onVenueChange" class="form-input">
                <option value="">— Seleccionar —</option>
                <option value="Teatro Babilonia - El Pinar (Sala)">Teatro Babilonia - El Pinar (Sala)</option>
                <option value="Playa - Rambla: Bajada 30">Playa - Rambla: Bajada 30</option>
              </select>
            </div>
            <div class="form-group">
              <label>Ubicación URL (Google Maps)</label>
              <input v-model="form.venue_map_url" type="text" class="form-input" placeholder="https://maps.app.goo.gl/...">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Categoría</label>
              <select v-model="form.categoria" class="form-input">
                <option>Teatro</option>
                <option>Música</option>
                <option>Danza</option>
                <option>Circo</option>
                <option>Cine</option>
                <option>Otro</option>
              </select>
            </div>
            <div class="form-group">
              <label>Precio (UYU)</label>
              <input v-model.number="form.precio" type="number" class="form-input" placeholder="400">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Imagen</label>
              <div class="image-upload-wrap">
                <div v-if="form.imagen" class="image-preview">
                  <img :src="form.imagen" alt="Preview">
                  <button class="btn-remove-img" @click="form.imagen = ''" title="Quitar imagen">✕</button>
                </div>
                <label class="btn-upload-file" :class="{ uploading: imageUploading }">
                  <input type="file" accept="image/*" @change="uploadImage" :disabled="imageUploading" style="display:none">
                  {{ imageUploading ? 'Subiendo...' : (form.imagen ? '🔄 Cambiar imagen' : '📁 Subir imagen') }}
                </label>
                <span v-if="imageError" class="field-error">{{ imageError }}</span>
                <input v-if="form.imagen" v-model="form.imagen" type="text" class="form-input" style="margin-top:6px; font-size:12px; color:#888;">
              </div>
            </div>
            <div class="form-group">
              <label>RedTickets URL</label>
              <input v-model="form.redtickets" type="text" class="form-input" placeholder="https://redtickets.uy/evento/...">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Edad mínima</label>
              <input v-model="form.edad" type="text" class="form-input" placeholder="apto para todo público">
            </div>
            <div class="form-group form-checkboxes">
              <label class="checkbox-label"><input type="checkbox" v-model="form.festival"> Festival</label>
              <label class="checkbox-label"><input type="checkbox" v-model="form.special"> Especial (ancho completo)</label>
              <label class="checkbox-label"><input type="checkbox" v-model="form.is_active"> Activa</label>
            </div>
          </div>
          <div class="form-group">
            <label>Descripción</label>
            <div v-for="(p, i) in form.descripcion" :key="i" class="descripcion-row">
              <textarea v-model="form.descripcion[i]" class="form-input form-textarea" rows="2" :placeholder="`Párrafo ${i + 1}`"></textarea>
              <button class="btn-remove-p" @click="removeParrafo(i)" title="Eliminar párrafo">✕</button>
            </div>
            <button class="btn-add-p" @click="addParrafo">+ Agregar párrafo</button>
          </div>
          <p v-if="modalError" class="modal-error">{{ modalError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeFuncionModal">Cancelar</button>
          <button class="btn-primary" @click="saveFuncion" :disabled="modalSaving">
            {{ modalSaving ? 'Guardando...' : (editingFuncion ? 'Guardar cambios' : 'Crear función') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── DELETE CONFIRM MODAL ── -->
    <div v-if="showDeleteConfirm" class="funcion-modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="funcion-modal funcion-modal-sm">
        <div class="modal-header">
          <h3>Eliminar función</h3>
          <button class="modal-close" @click="showDeleteConfirm = false">✕</button>
        </div>
        <div class="modal-body">
          <p style="color:#ccc;">¿Eliminar <strong style="color:#fff;">{{ deletingFuncion?.titulo }}</strong>?</p>
          <p style="color:#888; font-size:13px;">Los tickets vendidos no se eliminarán (soft delete).</p>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showDeleteConfirm = false">Cancelar</button>
          <button class="btn-danger" @click="deleteFuncion" :disabled="modalSaving">
            {{ modalSaving ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── EDIT USER MODAL ── -->
    <Teleport to="body">
    <div v-if="showEditUserModal" class="modal-overlay" @click.self="showEditUserModal = false">
      <div class="invite-modal">
        <h3 class="invite-modal-title">Editar usuario</h3>
        <div class="modal-form">
          <p style="color:#888;font-size:13px;margin:0 0 16px;">{{ editUserForm.email }}</p>
          <div class="modal-group">
            <label>Rol</label>
            <select v-model="editUserForm.role" class="modal-input">
              <option value="member">Miembro</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div class="modal-group">
            <label>Permisos</label>
            <div class="permissions-checks">
              <label v-for="perm in availablePermissions" :key="perm" class="perm-check">
                <input type="checkbox" :value="perm" v-model="editUserForm.permissions" />
                {{ perm }}
              </label>
            </div>
          </div>
          <p v-if="editUserError" style="color:#e74c3c;font-size:13px;">{{ editUserError }}</p>
          <p v-if="editUserSuccess" style="color:#2ecc71;font-size:13px;">✓ Usuario actualizado</p>
          <div class="modal-actions">
            <button class="btn-cancel-modal" @click="showEditUserModal = false">Cancelar</button>
            <button class="btn-send-invite" @click="saveEditUser" :disabled="editUserLoading">
              {{ editUserLoading ? 'Guardando...' : 'Guardar cambios' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- ── INVITE MODAL ── -->
    <div v-if="showInviteModal" class="modal-overlay" @click.self="showInviteModal = false">
      <div class="invite-modal">
        <h3 class="invite-modal-title">Invitar usuario</h3>
        <div class="modal-form">
          <div class="modal-group">
            <label>Email</label>
            <input type="email" v-model="inviteForm.email" class="modal-input" placeholder="email@ejemplo.com" />
          </div>
          <div class="modal-group">
            <label>Rol</label>
            <select v-model="inviteForm.role" class="modal-input">
              <option value="member">Miembro</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div class="modal-group">
            <label>Permisos</label>
            <div class="permissions-checks">
              <label v-for="p in availablePermissions" :key="p" class="perm-check">
                <input type="checkbox" :value="p" v-model="inviteForm.permissions" />
                {{ p }}
              </label>
            </div>
          </div>
          <p v-if="inviteError" class="invite-error">{{ inviteError }}</p>
          <p v-if="inviteSuccess" class="invite-success">✓ Invitación enviada a {{ inviteForm.email }}</p>
          <div class="modal-actions">
            <button class="btn-cancel-modal" @click="showInviteModal = false">Cancelar</button>
            <button class="btn-send-invite" @click="sendInvite" :disabled="inviteLoading">
              {{ inviteLoading ? 'Enviando...' : 'Enviar invitación' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── SISTEMA TAB ── -->
    <div v-if="activeTab === 'sistema'" class="dashboard-content">
      <div class="tab-header">
        <h2 class="tab-title">🖥️ Estado del Sistema</h2>
        <button @click="fetchHealth" class="refresh-btn" :class="{ spinning: healthLoading }">🔄</button>
      </div>

      <div v-if="healthLoading" class="tab-loading">Cargando estado...</div>
      <div v-else-if="healthError" class="no-data">{{ healthError }}</div>

      <div v-else-if="health" class="health-grid">

        <!-- Services -->
        <div class="health-card">
          <h3 class="health-card-title">⚙️ Servicios</h3>
          <div class="health-row" v-for="(active, name) in health.services" :key="name">
            <span class="health-label">{{ name }}</span>
            <span class="health-badge" :class="active ? 'badge-ok' : 'badge-error'">
              {{ active ? '✓ activo' : '✗ caído' }}
            </span>
          </div>
          <div class="health-row">
            <span class="health-label">postgresql (DB)</span>
            <span class="health-badge" :class="health.database.connected ? 'badge-ok' : 'badge-error'">
              {{ health.database.connected ? '✓ conectado' : '✗ error' }}
            </span>
          </div>
        </div>

        <!-- Resources -->
        <div class="health-card">
          <h3 class="health-card-title">💾 Recursos</h3>
          <template v-if="health.resources.disk">
            <div class="health-row">
              <span class="health-label">Disco usado</span>
              <span class="health-value">{{ health.resources.disk.used }} / {{ health.resources.disk.total }} ({{ health.resources.disk.percent }})</span>
            </div>
          </template>
          <template v-if="health.resources.memory">
            <div class="health-row">
              <span class="health-label">Memoria usada</span>
              <span class="health-value">{{ health.resources.memory.used_mb }}MB / {{ health.resources.memory.total_mb }}MB ({{ health.resources.memory.percent }})</span>
            </div>
            <div class="health-row">
              <span class="health-label">Memoria libre</span>
              <span class="health-value">{{ health.resources.memory.free_mb }}MB</span>
            </div>
          </template>
        </div>

        <!-- Tickets -->
        <div class="health-card">
          <h3 class="health-card-title">🎫 Tickets</h3>
          <div class="health-row">
            <span class="health-label">Hoy</span>
            <span class="health-value">{{ health.tickets.today }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Últimos 7 días</span>
            <span class="health-value">{{ health.tickets.week }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Últimos 30 días</span>
            <span class="health-value">{{ health.tickets.month }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Total</span>
            <span class="health-value">{{ health.tickets.total }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Online (MP)</span>
            <span class="health-value">{{ health.tickets.mp_total }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Recaudado (30d)</span>
            <span class="health-value" style="color:#c9a227;">$ {{ health.tickets.revenue_month.toLocaleString() }} UYU</span>
          </div>
        </div>

        <!-- Last ticket -->
        <div class="health-card" v-if="health.tickets.last">
          <h3 class="health-card-title">🕐 Último ticket online</h3>
          <div class="health-row">
            <span class="health-label">Función</span>
            <span class="health-value">{{ health.tickets.last.funcion }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Estado MP</span>
            <span class="health-badge badge-ok">{{ health.tickets.last.mp_status }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Creado</span>
            <span class="health-value">{{ new Date(health.tickets.last.created_at).toLocaleString('es-UY') }}</span>
          </div>
        </div>

        <!-- Contacts -->
        <div class="health-card">
          <h3 class="health-card-title">👥 Contactos</h3>
          <div class="health-row">
            <span class="health-label">Total en CRM</span>
            <span class="health-value">{{ health.contacts.total }}</span>
          </div>
          <div class="health-row">
            <span class="health-label">Entorno</span>
            <span class="health-badge" :class="health.environment === 'production' ? 'badge-ok' : 'badge-warn'">
              {{ health.environment }}
            </span>
          </div>
          <div class="health-row">
            <span class="health-label">Actualizado</span>
            <span class="health-value" style="font-size:11px;">{{ new Date(health.timestamp).toLocaleString('es-UY') }}</span>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
/* global Chart */
const API_BASE = '/api'

const VENUE_URLS = {
  'Teatro Babilonia - El Pinar (Sala)': 'https://maps.app.goo.gl/kQL5XygdunFnacNf9',
  'Playa - Rambla: Bajada 30': 'https://maps.app.goo.gl/b1CQ3BuSKiwiXD5N8'
}

const emptyForm = () => ({
  titulo: '',
  origen: '',
  date: '',
  date_display_input: '',
  date_error: '',
  fecha_display: '',
  venue_name: 'Teatro Babilonia - El Pinar (Sala)',
  venue_map_url: 'https://maps.app.goo.gl/kQL5XygdunFnacNf9',
  descripcion: [''],
  edad: 'apto para todo público',
  precio: 400,
  imagen: '',
  redtickets: '',
  categoria: 'Teatro',
  special: false,
  festival: true,
  is_active: true
})

export default {
  name: 'AdminDashboard',
  data() {
    return {
      // Tabs
      activeTab: 'analytics',
      allTabs: [
        { id: 'analytics', label: '📈 Analytics', permission: 'analytics' },
        { id: 'suscriptores', label: '📧 Suscriptores', permission: 'analytics' },
        { id: 'usuarios', label: '👥 Usuarios', permission: null },
        { id: 'funciones', label: '🎭 Funciones', permission: 'funciones_admin' },
        { id: 'horas', label: '⏱ Horas', permission: 'horas_user' },
        { id: 'sistema', label: '🖥️ Sistema', permission: null }
      ],

      // Analytics
      data: null,
      loading: false,
      selectedDays: 30,
      lastUpdated: '',
      charts: {},
      sourceColors: ['#c9a227', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#e67e22', '#1abc9c', '#f39c12'],
      deviceColors: ['#3498db', '#c9a227', '#2ecc71'],

      // Suscriptores
      suscriptores: [],
      suscriptoresLoading: false,
      suscriptoresFetched: false,
      filterSource: '',
      filterActive: '',
      filterSearch: '',

      // Usuarios
      usuarios: [],
      usuariosLoading: false,
      usuariosFetched: false,
      showEditUserModal: false,
      editUserForm: { id: null, email: '', role: 'member', permissions: [] },
      health: null,
      healthLoading: false,
      healthError: null,
      editUserLoading: false,
      editUserError: '',
      editUserSuccess: false,
      showInviteModal: false,
      inviteForm: { email: '', role: 'member', permissions: [] },
      inviteLoading: false,
      inviteError: '',
      inviteSuccess: false,

      // Funciones
      funciones: [],
      funcionesLoading: false,
      imageUploading: false,
      imageError: '',
      showFuncionModal: false,
      editingFuncion: null,
      form: emptyForm(),
      modalSaving: false,
      modalError: '',
      showDeleteConfirm: false,
      deletingFuncion: null,

      // Horas
      horasSummary: [],
      horasLoading: false,
      horasFilter: 'week',   // 'week' | 'month' | 'all'
    }
  },
  computed: {
    currentUserEmail() {
      return JSON.parse(localStorage.getItem('babilonia_auth_user') || '{}').email || ''
    },
    currentUserPerms() {
      return JSON.parse(localStorage.getItem('babilonia_auth_user') || '{}').permissions || []
    },
    currentUserRole() {
      return JSON.parse(localStorage.getItem('babilonia_auth_user') || '{}').role || ''
    },
    visibleTabs() {
      const role = this.currentUserRole
      const perms = this.currentUserPerms
      return this.allTabs.filter(tab => {
        if (role === 'admin') return true
        if (tab.id === 'usuarios') return role === 'admin'
        if (tab.id === 'funciones') return perms.includes('funciones_admin') || role === 'admin'
        if (tab.id === 'analytics' || tab.id === 'suscriptores') return perms.includes('analytics') || role === 'admin'
        if (tab.id === 'horas') return perms.includes('horas_user') || perms.includes('horas_admin') || role === 'admin'
        if (tab.id === 'sistema') return role === 'admin'
        return false
      })
    },
    availablePermissions() {
      return ['boleteria', 'cantina_admin', 'cantina_member', 'prensa', 'funciones_admin', 'horas_user', 'horas_admin']
    },
    availableSources() {
      return [...new Set(this.suscriptores.map(s => s.source).filter(Boolean))]
    },
    filteredSuscriptores() {
      return this.suscriptores.filter(s => {
        if (this.filterSource && s.source !== this.filterSource) return false
        if (this.filterActive === 'true' && !s.wants_news) return false
        if (this.filterActive === 'false' && s.wants_news) return false
        if (this.filterSearch) {
          const q = this.filterSearch.toLowerCase()
          const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase()
          if (!s.email.toLowerCase().includes(q) && !name.includes(q)) return false
        }
        return true
      })
    },
    totalSponsorClicks() {
      if (!this.data) return 0
      return this.data.sponsor_clicks.reduce((sum, s) => sum + s.clicks, 0)
    },
    maxPageViews() {
      if (!this.data || !this.data.pages.length) return 1
      return this.data.pages[0].views
    },
    maxSponsorClicks() {
      if (!this.data || !this.data.sponsor_clicks.length) return 1
      return this.data.sponsor_clicks[0].clicks
    },
    maxOsCount() {
      if (!this.data || !this.data.operating_systems || !this.data.operating_systems.length) return 1
      return this.data.operating_systems[0].count
    },
    maxBrowserCount() {
      if (!this.data || !this.data.browsers || !this.data.browsers.length) return 1
      return this.data.browsers[0].count
    }
  },
  async mounted() {
    const { authStore } = await import('@/store/auth.js')
    if (!authStore.isLoggedIn) {
      this.$router.push('/login?redirect=/admin/dashboard')
      return
    }
    const user = authStore.currentUser
    const perms = user.permissions || []
    const canAccess = user.role === 'admin' || perms.includes('analytics') || perms.includes('funciones_admin')
    if (!canAccess) {
      this.$router.push('/mi-cuenta')
      return
    }
    if (user.role !== 'admin' && !perms.includes('analytics') && perms.includes('funciones_admin')) {
      this.switchTab('funciones')
    }
    this.loadChartJS()
    this.fetchFunciones() // needed for formatPath in analytics
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab
      if (tab === 'sistema') this.fetchHealth()
      if (tab === 'suscriptores') this.fetchSuscriptores()
      if (tab === 'usuarios') this.fetchUsuarios()
      if (tab === 'funciones' && this.funciones.length === 0) this.fetchFunciones()
      if (tab === 'horas') this.fetchHoras()
    },

    // ── Analytics ──────────────────────────────────────────

    async fetchHoras() {
      this.horasLoading = true
      const { authStore } = await import('@/store/auth.js')
      const token = authStore.token
      try {
        const params = new URLSearchParams()
        const now = new Date()
        if (this.horasFilter === 'week') {
          const weekAgo = new Date(now)
          weekAgo.setDate(weekAgo.getDate() - 7)
          params.set('from', weekAgo.toISOString().split('T')[0])
        } else if (this.horasFilter === 'month') {
          const monthAgo = new Date(now)
          monthAgo.setMonth(monthAgo.getMonth() - 1)
          params.set('from', monthAgo.toISOString().split('T')[0])
        }
        const res = await fetch(`/api/horas/report?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          this.horasSummary = data.summary
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.horasLoading = false
      }
    },

    loadChartJS() {
      if (window.Chart) { this.fetchData(); return }
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js'
      script.onload = () => this.fetchData()
      document.head.appendChild(script)
    },

    async fetchData() {
      this.loading = true
      const { authStore } = await import('@/store/auth.js')
      const token = authStore.token
      try {
        const res = await fetch(`${API_BASE}/analytics/summary?days=${this.selectedDays}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.status === 401) {
          authStore.logout()
          this.$router.push('/login?redirect=/admin/dashboard')
          return
        }
        this.data = await res.json()
        this.lastUpdated = new Date().toLocaleString('es-UY')
        this.$nextTick(() => this.renderCharts())
      } catch {
        this.data = null
      }
      this.loading = false
    },

    renderCharts() {
      this.renderDailyChart()
      this.renderSourcesChart()
      this.renderHourlyChart()
      this.renderDeviceChart()
    },

    renderDailyChart() {
      const ctx = this.$refs.dailyChart
      if (!ctx) return
      if (this.charts.daily) this.charts.daily.destroy()
      this.charts.daily = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.data.daily_views.map(d => {
            const date = new Date(d.date + 'T12:00:00')
            return date.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })
          }),
          datasets: [{
            label: 'Visitas',
            data: this.data.daily_views.map(d => d.views),
            borderColor: '#c9a227',
            backgroundColor: 'rgba(201, 162, 39, 0.1)',
            fill: true, tension: 0.4, pointRadius: 4,
            pointBackgroundColor: '#c9a227', pointBorderColor: '#000', pointBorderWidth: 2
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { backgroundColor: '#111', titleColor: '#c9a227', bodyColor: '#fff', borderColor: '#333', borderWidth: 1 } },
          scales: {
            x: { grid: { color: '#1a1a1a' }, ticks: { color: '#666' } },
            y: { beginAtZero: true, grid: { color: '#1a1a1a' }, ticks: { color: '#666', stepSize: 1 } }
          }
        }
      })
    },

    renderSourcesChart() {
      const ctx = this.$refs.sourcesChart
      if (!ctx || !this.data.traffic_sources.length) return
      if (this.charts.sources) this.charts.sources.destroy()
      this.charts.sources = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.data.traffic_sources.map(s => s.source),
          datasets: [{ data: this.data.traffic_sources.map(s => s.count), backgroundColor: this.sourceColors, borderColor: '#111', borderWidth: 3 }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: '#111', titleColor: '#c9a227', bodyColor: '#fff', borderColor: '#333', borderWidth: 1 } }, cutout: '65%' }
      })
    },

    renderHourlyChart() {
      const ctx = this.$refs.hourlyChart
      if (!ctx) return
      if (this.charts.hourly) this.charts.hourly.destroy()
      const hourlyMap = {}
      for (let i = 0; i < 24; i++) hourlyMap[i] = 0
      this.data.hourly_distribution.forEach(h => { hourlyMap[h.hour] = h.views })
      this.charts.hourly = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: Object.keys(hourlyMap).map(h => `${h}h`),
          datasets: [{ label: 'Visitas', data: Object.values(hourlyMap), backgroundColor: 'rgba(201, 162, 39, 0.6)', borderColor: '#c9a227', borderWidth: 1, borderRadius: 3 }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: '#111', titleColor: '#c9a227', bodyColor: '#fff' } }, scales: { x: { grid: { display: false }, ticks: { color: '#666', font: { size: 10 } } }, y: { beginAtZero: true, grid: { color: '#1a1a1a' }, ticks: { color: '#666', stepSize: 1 } } } }
      })
    },

    renderDeviceChart() {
      const ctx = this.$refs.deviceChart
      if (!ctx || !this.data.devices || !this.data.devices.length) return
      if (this.charts.device) this.charts.device.destroy()
      this.charts.device = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.data.devices.map(d => d.device),
          datasets: [{ data: this.data.devices.map(d => d.count), backgroundColor: this.deviceColors, borderColor: '#111', borderWidth: 3 }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: '#111', titleColor: '#c9a227', bodyColor: '#fff', borderColor: '#333', borderWidth: 1 } }, cutout: '65%' }
      })
    },

    formatPath(path) {
      const funcionMatch = path.match(/^\/funcion\/([^/]+)$/)
      if (funcionMatch) {
        const val = funcionMatch[1]
        // Try slug match first
        const bySlug = this.funciones.find(f => f.slug === val)
        if (bySlug) return bySlug.titulo
        // Try numeric id match (legacy paths)
        const byId = this.funciones.find(f => String(f.id) === val)
        if (byId) return byId.titulo
        return `Función: ${val}`
      }
      const names = {
        '/': 'Inicio', '/cartelera': 'Cartelera', '/galeria': 'Galería',
        '/el-grupo': 'El Grupo', '/obras': 'Obras',
        '/talleres/festival': 'Talleres Festival', '/talleres/anual': 'Taller Anual'
      }
      return names[path] || path
    },

    barWidth(value, max) {
      return max > 0 ? Math.max((value / max) * 100, 8) : 0
    },

    // ── Auth ───────────────────────────────────────────────

    async logout() {
      const { authStore } = await import('@/store/auth.js')
      authStore.logout()
      this.$router.push('/')
    },

    // ── Suscriptores ───────────────────────────────────────

    async fetchSuscriptores() {
      if (this.suscriptoresFetched) return
      this.suscriptoresLoading = true
      try {
        const { authStore } = await import('@/store/auth.js')
        const res = await fetch(`${API_BASE}/admin/contacts`, {
          headers: { Authorization: `Bearer ${authStore.token}` }
        })
        if (res.ok) {
          const data = await res.json()
          this.suscriptores = data.contacts || []
          this.suscriptoresFetched = true
        }
      } catch (e) { console.error(e) } finally { this.suscriptoresLoading = false }
    },

    exportSuscriptores() {
      const headers = ['ID', 'Email', 'Nombre', 'Apellido', 'Teléfono', 'Fuente', 'Activo', 'Fecha']
      const rows = this.suscriptores.map(s => [
        s.id, s.email, s.first_name || '', s.last_name || '',
        s.phone || '', s.source, s.wants_news ? 'Sí' : 'No', this.formatDate(s.created_at)
      ])
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `suscriptores_${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },

    clearFilters() {
      this.filterSource = ''
      this.filterActive = ''
      this.filterSearch = ''
    },

    formatDate(isoStr) {
      if (!isoStr) return '—'
      return new Date(isoStr).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    // ── Usuarios ───────────────────────────────────────────

    async fetchUsuarios() {
      if (this.usuariosFetched) return
      this.usuariosLoading = true
      try {
        const { authStore } = await import('@/store/auth.js')
        if (!authStore.token) { this.usuariosLoading = false; return }
        const res = await fetch(`${API_BASE}/auth/admin/users`, {
          headers: { Authorization: `Bearer ${authStore.token}` }
        })
        if (res.ok) {
          const data = await res.json()
          this.usuarios = data.users || []
          this.usuariosFetched = true
        }
      } catch (e) { console.error(e) } finally { this.usuariosLoading = false }
    },

    openEditUser(user) {
      this.editUserForm = { id: user.id, email: user.email, role: user.role, permissions: [...(user.permissions || [])] }
      this.editUserError = ''
      this.editUserSuccess = false
      this.showEditUserModal = true
    },

    async saveEditUser() {
      this.editUserLoading = true
      this.editUserError = ''
      this.editUserSuccess = false
      try {
        const { authStore } = await import('@/store/auth.js')
        const res = await fetch(`${API_BASE}/auth/admin/users/${this.editUserForm.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authStore.token}` },
          body: JSON.stringify({ role: this.editUserForm.role, permissions: this.editUserForm.permissions })
        })
        const data = await res.json()
        if (res.ok) {
          const idx = this.usuarios.findIndex(u => u.id === this.editUserForm.id)
          if (idx !== -1) {
            this.usuarios[idx].role = this.editUserForm.role
            this.usuarios[idx].permissions = this.editUserForm.permissions
          }
          this.editUserSuccess = true
          setTimeout(() => { this.showEditUserModal = false }, 1000)
        } else {
          this.editUserError = data.error || 'Error al guardar'
        }
      } catch (e) { this.editUserError = 'Error de conexión' } finally { this.editUserLoading = false }
    },

    async deleteUser(user) {
      if (!confirm(`¿Eliminar la cuenta de ${user.email}?`)) return
      try {
        const { authStore } = await import('@/store/auth.js')
        const res = await fetch(`${API_BASE}/auth/admin/users/${user.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authStore.token}` }
        })
        if (res.ok) {
          this.usuarios = this.usuarios.filter(u => u.id !== user.id)
        } else {
          const data = await res.json()
          alert(data.error || 'Error al eliminar')
        }
      } catch (e) { alert('Error de conexión') }
    },

    async sendInvite() {
      this.inviteError = ''
      this.inviteSuccess = false
      if (!this.inviteForm.email) { this.inviteError = 'El email es obligatorio'; return }
      this.inviteLoading = true
      try {
        const { authStore } = await import('@/store/auth.js')
        const res = await fetch(`${API_BASE}/auth/admin/users/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authStore.token}` },
          body: JSON.stringify(this.inviteForm)
        })
        const data = await res.json()
        if (res.ok) {
          this.inviteSuccess = true
          if (data.user) this.usuarios.unshift(data.user)
          this.inviteForm = { email: '', role: 'member', permissions: [] }
          setTimeout(() => { this.showInviteModal = false; this.inviteSuccess = false }, 2000)
        } else {
          this.inviteError = data.error || 'Error al enviar invitación'
        }
      } catch (e) { this.inviteError = 'Error de conexión' } finally { this.inviteLoading = false }
    },

    // ── Funciones ──────────────────────────────────────────

    async fetchFunciones() {
      this.funcionesLoading = true
      try {
        const res = await fetch(`${API_BASE}/funciones`)
        if (res.ok) this.funciones = await res.json()
      } catch (e) { console.error('Error loading funciones:', e) }
      this.funcionesLoading = false
    },

    isPast(dateStr) {
      return new Date(dateStr + 'T23:59:59') < new Date()
    },

    openCreateModal() {
      this.editingFuncion = null
      this.form = emptyForm()
      this.modalError = ''
      this.imageError = ''
      this.showFuncionModal = true
    },

    openEditModal(f) {
      this.editingFuncion = f
      let dateInput = ''
      if (f.date) {
        const parts = f.date.split('-')
        if (parts.length === 3) dateInput = `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      this.form = {
        titulo: f.titulo || '', origen: f.origen || '',
        date: f.date || '', date_display_input: dateInput, date_error: '',
        fecha_display: f.fecha_display || '',
        venue_name: f.venue?.name || '', venue_map_url: f.venue?.mapUrl || '',
        descripcion: f.descripcion?.length ? [...f.descripcion] : [''],
        edad: f.edad || 'apto para todo público',
        precio: f.precio || 400, imagen: f.imagen || '', redtickets: f.redtickets || '',
        categoria: f.categoria || 'Teatro', special: f.special || false,
        festival: f.festival !== false, is_active: f.is_active !== false
      }
      this.modalError = ''
      this.imageError = ''
      this.showFuncionModal = true
    },

    closeFuncionModal() {
      this.showFuncionModal = false
      this.editingFuncion = null
      this.modalError = ''
    },

    addParrafo() { this.form.descripcion.push('') },

    removeParrafo(i) {
      if (this.form.descripcion.length > 1) this.form.descripcion.splice(i, 1)
    },

    autoFormatDate(e) {
      let val = e.target.value.replace(/\D/g, '')
      if (val.length >= 3) val = val.slice(0,2) + '/' + val.slice(2)
      if (val.length >= 6) val = val.slice(0,5) + '/' + val.slice(5)
      this.form.date_display_input = val.slice(0, 10)
    },

    parseDateInput() {
      const val = (this.form.date_display_input || '').trim()
      if (!val) { this.form.date = ''; this.form.date_error = ''; return }
      const match = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
      if (!match) { this.form.date_error = 'Formato inválido. Usá dd/mm/aaaa'; this.form.date = ''; return }
      const [, d, m, y] = match
      const iso = `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`
      if (isNaN(new Date(iso).getTime())) { this.form.date_error = 'Fecha inválida'; this.form.date = ''; return }
      this.form.date = iso
      this.form.date_error = ''
    },

    onVenueChange() {
      const url = VENUE_URLS[this.form.venue_name]
      if (url) this.form.venue_map_url = url
    },

    async uploadImage(event) {
      const file = event.target.files[0]
      if (!file) return
      this.imageUploading = true
      this.imageError = ''
      const { authStore } = await import('@/store/auth.js')
      const formData = new FormData()
      formData.append('file', file)
      try {
        const res = await fetch(`${API_BASE}/funciones/upload-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authStore.token}` },
          body: formData
        })
        const data = await res.json()
        if (!res.ok) { this.imageError = data.error || 'Error al subir imagen.'; return }
        this.form.imagen = data.path
      } catch (e) {
        this.imageError = 'Error de conexión al subir imagen.'
      } finally {
        this.imageUploading = false
        event.target.value = ''
      }
    },

    async saveFuncion() {
      this.parseDateInput()
      if (!this.form.titulo || !this.form.date || !this.form.fecha_display) {
        this.modalError = 'Título, fecha y fecha display son obligatorios.'
        return
      }
      if (this.form.date_error) { this.modalError = this.form.date_error; return }

      this.modalSaving = true
      this.modalError = ''
      const { authStore } = await import('@/store/auth.js')
      const payload = { ...this.form, descripcion: this.form.descripcion.filter(p => p.trim() !== '') }

      try {
        let res
        if (this.editingFuncion) {
          res = await fetch(`${API_BASE}/funciones/${this.editingFuncion.slug}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authStore.token}` },
            body: JSON.stringify(payload)
          })
        } else {
          res = await fetch(`${API_BASE}/funciones`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authStore.token}` },
            body: JSON.stringify(payload)
          })
        }
        if (!res.ok) {
          const err = await res.json()
          this.modalError = err.error || 'Error al guardar.'
          return
        }
        await this.fetchFunciones()
        this.closeFuncionModal()
      } catch (e) {
        this.modalError = 'Error de conexión.'
      } finally {
        this.modalSaving = false
      }
    },

    confirmDelete(f) {
      this.deletingFuncion = f
      this.showDeleteConfirm = true
    },

    async deleteFuncion() {
      this.modalSaving = true
      const { authStore } = await import('@/store/auth.js')
      try {
        const res = await fetch(`${API_BASE}/funciones/${this.deletingFuncion.slug}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authStore.token}` }
        })
        if (res.ok) {
          await this.fetchFunciones()
          this.showDeleteConfirm = false
          this.deletingFuncion = null
        }
      } catch (e) {
        console.error('Error deleting funcion:', e)
      } finally {
        this.modalSaving = false
      }
    },

    async fetchHealth() {
      this.healthLoading = true
      this.healthError = null
      try {
        const res = await fetch('/api/admin/health', {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (!res.ok) throw new Error('Error fetching health')
        this.health = await res.json()
      } catch (e) {
        this.healthError = 'No se pudo cargar el estado del sistema.'
        console.error(e)
      } finally {
        this.healthLoading = false
      }
    }
  }
}
</script>

<style scoped>
.admin-dashboard { min-height: 100vh; background: #0a0a0a; color: #fff; }

/* Header */
.admin-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 30px; background: #111; border-bottom: 1px solid #222; position: sticky; top: 0; z-index: 100; }
.header-left { display: flex; align-items: center; gap: 12px; }
.header-logo { font-size: 28px; }
.header-title { font-size: 20px; font-weight: bold; color: #c9a227; margin: 0; }
.header-right { display: flex; align-items: center; gap: 10px; }
.period-select { background: #1a1a1a; color: #fff; border: 1px solid #333; padding: 8px 12px; border-radius: 6px; font-size: 14px; cursor: pointer; }
.refresh-btn { background: none; border: 1px solid #333; border-radius: 6px; padding: 8px 10px; font-size: 16px; cursor: pointer; transition: all 0.3s; }
.refresh-btn:hover { border-color: #c9a227; }
.spinning { animation: spin 1s linear infinite; }
@keyframes spin { 100% { transform: rotate(360deg); } }
.logout-btn { background: #222; color: #888; border: 1px solid #333; padding: 8px 16px; border-radius: 6px; font-size: 14px; cursor: pointer; transition: all 0.3s; }
.logout-btn:hover { color: #e74c3c; border-color: #e74c3c; }

/* Tab Nav */
.tab-nav { display: flex; background: #111; border-bottom: 1px solid #222; padding: 0 30px; }
.tab-btn { background: none; border: none; border-bottom: 3px solid transparent; color: #666; padding: 14px 20px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.tab-btn:hover { color: #ccc; }
.tab-btn.active { color: #c9a227; border-bottom-color: #c9a227; }

/* Loading */
.loading-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; color: #666; }
.loader { width: 40px; height: 40px; border: 3px solid #222; border-top-color: #c9a227; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 15px; }

/* Dashboard Content */
.dashboard-content { padding: 25px 30px 60px; max-width: 1200px; margin: 0 auto; }

/* Tab header */
.tab-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 25px; }
.tab-title { font-size: 22px; font-weight: bold; color: #fff; margin: 0; }
.tab-loading { color: #888; padding: 20px 0; }
.tab-count { color: #aaa; font-size: 14px; margin: 0 0 16px; }

/* Buttons */
.btn-primary { background: #c9a227; color: #000; border: none; padding: 10px 20px; border-radius: 6px; font-size: 14px; font-weight: bold; cursor: pointer; transition: all 0.2s; }
.btn-primary:hover { background: #d4af37; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary { background: #222; color: #ccc; border: 1px solid #333; padding: 10px 20px; border-radius: 6px; font-size: 14px; cursor: pointer; }
.btn-secondary:hover { border-color: #555; color: #fff; }
.btn-danger { background: #e74c3c; color: #fff; border: none; padding: 10px 20px; border-radius: 6px; font-size: 14px; font-weight: bold; cursor: pointer; }
.btn-danger:hover { background: #c0392b; }
.btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-export { background: #222; border: 1px solid #333; color: #ccc; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 13px; }
.btn-export:hover { background: #333; color: #fff; }
.btn-invite { background: #c9a227; border: none; color: #000; font-weight: bold; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 13px; }
.btn-invite:hover { opacity: 0.85; }

/* Filters */
.filters-bar { display: flex; align-items: flex-end; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-label { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.05em; }
.filter-select, .filter-input { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; color: #ccc; padding: 7px 12px; font-size: 13px; min-width: 120px; }
.filter-select:focus, .filter-input:focus { outline: none; border-color: #c9a227; }
.btn-clear-filters { background: none; border: 1px solid #444; color: #888; padding: 7px 12px; border-radius: 6px; cursor: pointer; font-size: 12px; align-self: flex-end; }
.btn-clear-filters:hover { border-color: #e74c3c; color: #e74c3c; }

/* Data Table */
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th { text-align: left; padding: 10px 12px; color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #222; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #1a1a1a; color: #ccc; }
.data-table tr:hover td { background: #111; }
.source-badge { background: #1a1a2e; border: 1px solid #333; color: #888; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-family: monospace; }
.status-active { color: #2ecc71; font-size: 12px; }
.status-inactive { color: #e74c3c; font-size: 12px; }
.status-pending { color: #f39c12; font-size: 12px; }
.role-badge { padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
.role-badge.admin { background: #c9a227; color: #000; }
.role-badge.member { background: #333; color: #ccc; }
.perm-tag { display: inline-block; background: #1a1a2e; border: 1px solid #333; color: #888; padding: 1px 6px; border-radius: 3px; font-size: 10px; font-family: monospace; margin-right: 4px; }
.no-perms { color: #444; }
.btn-edit-user { background: none; border: 1px solid #333; color: #666; width: 24px; height: 24px; border-radius: 4px; cursor: pointer; font-size: 11px; margin-right: 4px; }
.btn-edit-user:hover { border-color: #c9a227; color: #c9a227; background: rgba(201, 162, 39, 0.1); }
.btn-delete-user { background: none; border: 1px solid #333; color: #555; width: 24px; height: 24px; border-radius: 4px; cursor: pointer; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; }
.btn-delete-user:hover { border-color: #e74c3c; color: #e74c3c; background: rgba(231, 76, 60, 0.1); }

/* KPI Cards */
.kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 25px; }
.kpi-card { background: #111; border: 1px solid #222; border-radius: 12px; padding: 20px; display: flex; align-items: center; gap: 15px; }
.kpi-card:hover { border-color: #333; }
.kpi-icon { font-size: 32px; }
.kpi-info { display: flex; flex-direction: column; }
.kpi-value { font-size: 32px; font-weight: bold; color: #fff; line-height: 1; }
.kpi-label { font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: 0.08em; margin-top: 4px; }

/* Charts */
.charts-row { margin-bottom: 20px; }
.charts-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.chart-card { background: #111; border: 1px solid #222; border-radius: 12px; padding: 25px; }
.chart-wide { width: 100%; }
.chart-title { font-size: 16px; font-weight: 600; color: #ccc; margin: 0 0 20px; }
.chart-container { position: relative; height: 250px; }
.ranking-list { display: flex; flex-direction: column; gap: 10px; }
.ranking-item { display: flex; align-items: center; gap: 10px; }
.ranking-pos { width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; background: #1a1a1a; border-radius: 50%; font-size: 11px; color: #666; flex-shrink: 0; }
.ranking-name { font-size: 14px; color: #ccc; min-width: 100px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ranking-bar-wrap { flex: 1; height: 8px; background: #1a1a1a; border-radius: 4px; overflow: hidden; }
.ranking-bar { height: 100%; background: #3498db; border-radius: 4px; transition: width 0.5s ease; }
.ranking-bar-gold { background: #c9a227; }
.ranking-bar-blue { background: #3498db; }
.ranking-bar-green { background: #2ecc71; }
.ranking-value { font-size: 14px; font-weight: bold; color: #fff; min-width: 30px; text-align: right; }
.no-data { color: #444; font-style: italic; font-size: 14px; margin: 0; }
.empty-card { border: none; background: transparent; }
.source-legend { margin-top: 20px; display: flex; flex-direction: column; gap: 8px; }
.legend-item { display: flex; align-items: center; gap: 10px; }
.legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.legend-label { font-size: 13px; color: #999; flex: 1; }
.legend-value { font-size: 13px; font-weight: bold; color: #fff; }
.last-updated { text-align: center; font-size: 12px; color: #444; margin-top: 30px; }
.error-screen { display: flex; align-items: center; justify-content: center; min-height: 60vh; color: #666; }
.retry-btn { background: #c9a227; color: #000; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-weight: bold; margin-left: 10px; }

/* Funciones Table */
.funciones-table-wrap { overflow-x: auto; }
.funciones-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.funciones-table th { text-align: left; padding: 12px 16px; background: #111; color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 1px solid #222; }
.funciones-table td { padding: 14px 16px; border-bottom: 1px solid #1a1a1a; vertical-align: middle; }
.funciones-table tr:hover td { background: #111; }
.row-past td { opacity: 0.45; }
.td-id { color: #555; width: 40px; }
.td-titulo { font-weight: 600; color: #fff; }
.td-origen { color: #888; font-weight: 400; font-size: 13px; }
.td-fecha { color: #aaa; white-space: nowrap; }
.td-venue { color: #888; font-size: 13px; }
.td-precio { color: #c9a227; font-weight: bold; }
.badge-yes { background: rgba(46, 204, 113, 0.15); color: #2ecc71; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: bold; }
.badge-no { background: rgba(255,255,255,0.05); color: #555; padding: 3px 10px; border-radius: 20px; font-size: 12px; }
.td-actions { display: flex; gap: 8px; white-space: nowrap; }
.btn-edit { background: rgba(201, 162, 39, 0.1); color: #c9a227; border: 1px solid rgba(201, 162, 39, 0.3); padding: 6px 12px; border-radius: 5px; font-size: 12px; cursor: pointer; }
.btn-edit:hover { background: rgba(201, 162, 39, 0.2); }
.btn-delete { background: rgba(231, 76, 60, 0.1); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.3); padding: 6px 12px; border-radius: 5px; font-size: 12px; cursor: pointer; }
.btn-delete:hover { background: rgba(231, 76, 60, 0.2); }

/* Funcion Modal */
.funcion-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.8); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
.funcion-modal { background: #111; border: 1px solid #333; border-radius: 12px; width: 100%; max-width: 700px; max-height: 90vh; display: flex; flex-direction: column; }
.funcion-modal-sm { max-width: 420px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 25px; border-bottom: 1px solid #222; }
.modal-header h3 { margin: 0; font-size: 18px; color: #fff; }
.modal-close { background: none; border: none; color: #666; font-size: 18px; cursor: pointer; padding: 4px 8px; }
.modal-close:hover { color: #fff; }
.modal-body { padding: 20px 25px; overflow-y: auto; flex: 1; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 25px; border-top: 1px solid #222; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group label { font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.06em; }
.form-input { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; color: #fff; padding: 10px 12px; font-size: 14px; outline: none; transition: border-color 0.2s; width: 100%; box-sizing: border-box; }
.form-input:focus { border-color: #c9a227; }
.form-textarea { resize: vertical; font-family: inherit; }
.form-checkboxes { flex-direction: column; justify-content: flex-end; gap: 10px; }
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #ccc; cursor: pointer; text-transform: none; letter-spacing: 0; }
.checkbox-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; accent-color: #c9a227; }
.descripcion-row { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 8px; }
.btn-remove-p { background: rgba(231, 76, 60, 0.1); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.3); border-radius: 5px; padding: 8px 10px; cursor: pointer; font-size: 12px; flex-shrink: 0; margin-top: 2px; }
.btn-add-p { background: rgba(255,255,255,0.05); color: #888; border: 1px dashed #444; border-radius: 6px; padding: 8px 16px; font-size: 13px; cursor: pointer; width: 100%; }
.btn-add-p:hover { color: #fff; border-color: #666; }
.modal-error { color: #e74c3c; font-size: 13px; margin-top: 12px; padding: 10px 14px; background: rgba(231, 76, 60, 0.1); border-radius: 6px; }

/* Image upload */
.image-upload-wrap { display: flex; flex-direction: column; gap: 8px; }
.image-preview { position: relative; width: 120px; }
.image-preview img { width: 120px; height: 80px; object-fit: cover; border-radius: 6px; border: 1px solid #333; }
.btn-remove-img { position: absolute; top: -6px; right: -6px; background: #e74c3c; color: #fff; border: none; border-radius: 50%; width: 20px; height: 20px; font-size: 11px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.btn-upload-file { display: inline-block; background: rgba(255,255,255,0.05); color: #aaa; border: 1px dashed #444; border-radius: 6px; padding: 10px 16px; font-size: 13px; cursor: pointer; transition: all 0.2s; text-align: center; }
.btn-upload-file:hover { color: #fff; border-color: #c9a227; }
.btn-upload-file.uploading { opacity: 0.5; cursor: not-allowed; }
.field-error { color: #e74c3c; font-size: 12px; }

/* User/Invite Modals */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.invite-modal { background: #111; border: 1px solid #222; border-radius: 12px; padding: 32px; width: 100%; max-width: 480px; }
.invite-modal-title { color: #c9a227; font-size: 18px; margin: 0 0 24px; }
.modal-form { display: flex; flex-direction: column; gap: 16px; }
.modal-group { display: flex; flex-direction: column; gap: 6px; }
.modal-group label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
.modal-input { background: #000; border: 1px solid #333; border-radius: 6px; color: #fff; padding: 10px 14px; font-size: 14px; width: 100%; box-sizing: border-box; }
.modal-input:focus { outline: none; border-color: #c9a227; }
.permissions-checks { display: flex; flex-wrap: wrap; gap: 10px; }
.perm-check { display: flex; align-items: center; gap: 6px; color: #ccc; font-size: 13px; cursor: pointer; text-transform: none; letter-spacing: 0; }
.perm-check input { accent-color: #c9a227; }
.invite-error { color: #e74c3c; font-size: 13px; margin: 0; }
.invite-success { color: #2ecc71; font-size: 13px; margin: 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; }
.btn-cancel-modal { background: #222; border: 1px solid #333; color: #888; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 14px; }
.btn-send-invite { background: #c9a227; border: none; color: #000; font-weight: bold; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 14px; }
.btn-send-invite:disabled { opacity: 0.6; cursor: not-allowed; }

/* Horas Tab */
.horas-filter-bar { display: flex; gap: 8px; margin-bottom: 24px; }
.horas-filter-btn { background: #1a1a1a; border: 1px solid #333; color: #666; padding: 7px 18px; border-radius: 20px; font-size: 13px; cursor: pointer; transition: all 0.2s; }
.horas-filter-btn.active { background: #c9a227; border-color: #c9a227; color: #000; font-weight: bold; }
.horas-filter-btn:hover:not(.active) { color: #fff; border-color: #555; }
.horas-total-banner { background: linear-gradient(135deg, #1a1500, #111); border: 1px solid #c9a22740; border-radius: 12px; padding: 24px 28px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: center; }
.horas-total-label { font-size: 14px; color: #888; text-transform: uppercase; letter-spacing: 0.1em; }
.horas-total-value { font-size: 48px; font-weight: bold; color: #c9a227; line-height: 1; }
.horas-members-grid { display: flex; flex-direction: column; gap: 12px; }
.horas-member-card { background: #111; border: 1px solid #1e1e1e; border-radius: 10px; padding: 18px 20px; }
.horas-member-name { font-size: 13px; color: #888; margin-bottom: 6px; }
.horas-member-hours { font-size: 32px; font-weight: bold; color: #fff; line-height: 1; }
.horas-unit { font-size: 18px; color: #666; margin-left: 2px; }
.horas-member-entries { font-size: 12px; color: #555; margin: 4px 0 12px; }
.horas-bar-wrap { height: 6px; background: #1a1a1a; border-radius: 3px; overflow: hidden; }
.horas-bar { height: 100%; background: #c9a227; border-radius: 3px; transition: width 0.5s ease; }

/* Mobile */
@media (max-width: 900px) {
  .admin-header { flex-direction: column; gap: 10px; padding: 15px 20px; }
  .dashboard-content { padding: 20px 15px 60px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .charts-two-col { grid-template-columns: 1fr; }
  .kpi-value { font-size: 24px; }
  .form-row { grid-template-columns: 1fr; }
  .tab-nav { padding: 0 15px; overflow-x: auto; }
  .tab-btn { font-size: 12px; padding: 10px 12px; }
  .filters-bar { flex-direction: column; align-items: flex-start; }
  .filter-select, .filter-input { width: 100%; }
}
@media (max-width: 480px) {
  .kpi-grid { grid-template-columns: 1fr; }
  .header-right { flex-wrap: wrap; justify-content: center; }
}

/* ── Sistema / Health ── */
.health-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.health-card {
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 20px;
}

.health-card-title {
  font-size: 14px;
  font-weight: bold;
  color: #c9a227;
  margin: 0 0 16px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.health-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #222;
  gap: 12px;
}

.health-row:last-child {
  border-bottom: none;
}

.health-label {
  font-size: 13px;
  color: #888;
  flex-shrink: 0;
}

.health-value {
  font-size: 13px;
  color: #fff;
  text-align: right;
}

.health-badge {
  font-size: 12px;
  font-weight: bold;
  padding: 3px 10px;
  border-radius: 20px;
}

.badge-ok {
  background: rgba(46, 204, 113, 0.15);
  color: #2ecc71;
  border: 1px solid rgba(46, 204, 113, 0.3);
}

.badge-error {
  background: rgba(231, 76, 60, 0.15);
  color: #e74c3c;
  border: 1px solid rgba(231, 76, 60, 0.3);
}

.badge-warn {
  background: rgba(201, 162, 39, 0.15);
  color: #c9a227;
  border: 1px solid rgba(201, 162, 39, 0.3);
}

</style>